// frontend/src/utils/bookingDraft.js
//
// KEEP THE CUSTOMER'S BOOKING WHILE THEY STEP AWAY TO BUY A PACKAGE (2026-10-26).
//
// THE PROBLEM. "Packages you can buy" appears on every booking step, but the buy action leaves the page. The
// booking form's contents live in React state - name, email, phone, the chosen date and time, group members,
// chosen products - and NONE of it is in the URL. Leaving to buy therefore used to mean coming back to an empty
// form, which is why the cards were informational only.
//
// THE RULE. A draft is scoped to one booking attempt (shop + professional + service) and lives in
// `sessionStorage`, so it dies with the tab: an abandoned booking does not leave a guest's name, email and phone
// sitting on the machine. It is cleared the moment the booking is confirmed.
//
// Versioned, because a draft written by an older build must be DISCARDED rather than half-restored into a shape
// the current page no longer expects.

const KEY_PREFIX = "ogestyle_booking_draft";
export const BOOKING_DRAFT_VERSION = 1;

/**
 * ── A DRAFT DIES BY ITSELF (reported 2026-10-27) ─────────────────────────────────────────────────────
 *
 * *"the persisting info stays in the system for so long a time that it pre-populates all the info and it MIGHT
 * be for a different user"* — and worse, it survived a booking that had been made and paid for, so the next
 * person at the same machine found somebody else's name, email and phone already filled in.
 *
 * Two things fix that, and they are different mechanisms on purpose:
 *
 *   • this TTL — a draft nobody has touched for half an hour is not an attempt in progress. It is refreshed on
 *     every save, so it measures IDLE time, not the length of a long booking;
 *   • `clearAllBookingStorage`, called when a booking is actually confirmed, so nothing at all is left behind
 *     by a completed one.
 *
 * `sessionStorage` alone was never enough: a browser restores it on "reopen closed tab", and a shared machine
 * usually keeps the window open.
 */
export const BOOKING_DRAFT_TTL_MINUTES = 30;

function scopeTag({ shopId, staffId, serviceId }) {
  return [shopId || "-", staffId || "-", serviceId || "-"].join("_");
}

export function bookingDraftKey(scope) {
  return `${KEY_PREFIX}:${scopeTag(scope || {})}`;
}

function storage() {
  try {
    return typeof window !== "undefined" ? window.sessionStorage : null;
  } catch (e) {
    return null;
  }
}

/**
 * ONLY WHAT THE CUSTOMER ACTUALLY ENTERED (2026-10-26).
 *
 * The first version wrote every field, including the empty ones. Restoring a field that was never filled is at
 * best noise and at worst a real defect: an empty saved value overrides whatever the page would otherwise
 * default to, and a draft from a half-filled attempt can put the customer on a step that has nothing to show.
 *
 * So the draft keeps the values that carry information and drops the rest — empty strings, nulls, empty
 * arrays and objects whose every value was itself empty. A field the customer never touched simply is not in
 * the draft, which means restore cannot overwrite it.
 */
function isEmptyValue(value) {
  if (value === null || value === undefined) return true;
  if (typeof value === "string") return value.trim() === "";
  if (Array.isArray(value)) return value.length === 0;
  if (typeof value === "object") return Object.keys(compactObject(value)).length === 0;
  return false;
}

function compactObject(input) {
  const out = {};
  for (const [key, value] of Object.entries(input || {})) {
    if (isEmptyValue(value)) continue;
    out[key] = value;
  }
  return out;
}

function compactDraft(data) {
  return compactObject(data || {});
}

/**
 * Save the parts of the booking form that would otherwise be lost. Never throws: a browser that refuses
 * sessionStorage (private mode, quota) must not break booking - it just means the customer re-enters details.
 */
export function saveBookingDraft(scope, data) {
  const store = storage();
  if (!store || !scope || (!scope.shopId && !scope.staffId)) return false;
  const compacted = compactDraft(data);
  // Nothing worth keeping: drop the entry rather than store an empty shell.
  if (!Object.keys(compacted).length) {
    forgetBookingDraft(scope);
    return false;
  }
  try {
    store.setItem(
      bookingDraftKey(scope),
      JSON.stringify({
        version: BOOKING_DRAFT_VERSION,
        savedAt: new Date().toISOString(),
        data: compacted,
      }),
    );
    return true;
  } catch (e) {
    return false;
  }
}

/** The draft for this booking attempt, or null when there is none / it is stale / it is from an older build. */
export function loadBookingDraft(scope) {
  const store = storage();
  if (!store || !scope) return null;
  try {
    const raw = store.getItem(bookingDraftKey(scope));
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.version !== BOOKING_DRAFT_VERSION || !parsed.data) {
      forgetBookingDraft(scope);
      return null;
    }
    // IDLE, NOT AGE. Every save refreshes `savedAt`, so this only discards an attempt nobody has touched for
    // half an hour — never a booking somebody is still filling in.
    if (isStale(parsed.savedAt)) {
      forgetBookingDraft(scope);
      return null;
    }
    return parsed.data;
  } catch (e) {
    return null;
  }
}

function isStale(savedAt) {
  const saved = new Date(savedAt || 0).getTime();
  if (!Number.isFinite(saved) || saved <= 0) return true;
  return Date.now() - saved > BOOKING_DRAFT_TTL_MINUTES * 60 * 1000;
}

function forgetBookingDraft(scope) {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(bookingDraftKey(scope));
  } catch (e) {
    /* nothing to do */
  }
}

/** Cleared once the booking is confirmed, so a later visit never resurrects a completed one. */
export function clearBookingDraft(scope) {
  forgetBookingDraft(scope);
  forgetPackagePurchase(scope);
}

/**
 * ── DID THEY BUY A PACKAGE DURING THIS BOOKING? (2026-10-27) ────────────────────────────────────────
 *
 * Agreement 2.8a: once a package is in play, the "Packages you can buy" offers must stop appearing — otherwise the
 * customer is invited to buy something they have already covered, on every step that follows.
 *
 * A purchase made from inside the booking is identifiable: the buy button sends `returnTo`, the server validates it
 * as an internal path, and the confirmation page renders only AFTER the Stripe session is verified. So the marker
 * is written at that verified moment, keyed on the same booking attempt the draft uses, and every later step reads
 * it.
 *
 * Deliberately a SEPARATE storage key from the draft: the draft is rewritten on every keystroke and would wipe a
 * flag stored inside it.
 */
const PURCHASE_KEY_PREFIX = "ogestyle_booking_purchased";

export function markPackagePurchasedDuringBooking(scope) {
  const store = storage();
  if (!store || !scope || (!scope.shopId && !scope.staffId)) return false;
  try {
    store.setItem(
      `${PURCHASE_KEY_PREFIX}:${scopeTag(scope)}`,
      JSON.stringify({ at: new Date().toISOString() }),
    );
    return true;
  } catch (e) {
    return false;
  }
}

export function wasPackagePurchasedDuringBooking(scope) {
  const store = storage();
  if (!store || !scope) return false;
  try {
    const raw = store.getItem(`${PURCHASE_KEY_PREFIX}:${scopeTag(scope)}`);
    if (!raw) return false;
    // The same idle rule as the draft: a marker from an abandoned attempt must not keep the offers hidden the
    // next time somebody books the same shop + professional + service.
    let at = null;
    try {
      at = JSON.parse(raw)?.at;
    } catch (e) {
      at = null;
    }
    if (!at) {
      // Written by the first version of this marker, which stored a bare timestamp string.
      at = raw;
    }
    if (isStale(at)) {
      forgetPackagePurchase(scope);
      return false;
    }
    return true;
  } catch (e) {
    return false;
  }
}

function forgetPackagePurchase(scope) {
  const store = storage();
  if (!store) return;
  try {
    store.removeItem(`${PURCHASE_KEY_PREFIX}:${scopeTag(scope)}`);
  } catch (e) {
    /* nothing to do */
  }
}

/**
 * THE BOOKING ATTEMPT A `returnTo` PATH BELONGS TO.
 *
 * The booking page carries its scope in the query string (`shopId`, `staffId`, `serviceId`), and `returnTo` is
 * exactly that path plus its query. Reading it back here is what makes the marker land on the same key the booking
 * page will look under — otherwise the confirmation page would have to guess, and a wrong guess reads as "they
 * never bought anything" and the offers come straight back.
 */
export function bookingScopeFromReturnTo(returnTo) {
  if (!returnTo || typeof returnTo !== "string") return null;
  let parsed;
  try {
    parsed = new URL(returnTo, "https://booking.invalid");
  } catch (e) {
    return null;
  }
  const scope = {
    shopId: parsed.searchParams.get("shopId") || "",
    staffId: parsed.searchParams.get("staffId") || "",
    serviceId: parsed.searchParams.get("serviceId") || "",
  };
  return scope.shopId || scope.staffId ? scope : null;
}

/**
 * ── AND NOTHING IS LEFT BEHIND BY A BOOKING THAT HAPPENED (2026-10-27) ──────────────────────────────
 *
 * Called when a booking is CONFIRMED. A completed booking is not an attempt in progress, and its draft holding a
 * name, an email and a phone number is exactly the leak the user reported: *"it MIGHT be for a different user"*.
 *
 * It clears EVERY booking scope, not just the one that just finished — the customer may have started a second
 * attempt for another professional in another tab, and a stale draft there has the same problem. There is never
 * more than a handful of them, and the alternative is a key-by-key sweep of storage that has to be got right.
 */
export function clearAllBookingStorage() {
  const store = storage();
  if (!store) return;
  try {
    const keys = [];
    for (let index = 0; index < store.length; index += 1) {
      const key = store.key(index);
      if (!key) continue;
      if (key.startsWith(KEY_PREFIX) || key.startsWith(PURCHASE_KEY_PREFIX)) keys.push(key);
    }
    keys.forEach((key) => store.removeItem(key));
  } catch (e) {
    /* nothing to do */
  }
}
